import { Box, Button } from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Edit from '@material-ui/icons/Edit';
import { divIcon, DivIcon, LatLng, Map } from 'leaflet';
import React, { ReactElement, useEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { Marker } from 'react-leaflet';
import { AttachmentType } from '../../../models';
import { getPreviewImage } from '../../../utils';
import {
	AttachmentPinIcon,
	Body1,
	ErrorBoundary,
	Header4,
	Header5,
	LinkButton,
	MapReference,
	VideoPlayer
} from '../../atoms';
import {
	FormAttachment,
	IMapAttachmentMapParams,
	MapViewer
} from '../../organisms';

import './attachment-editor-overview.styles.scss';

export interface IAttachmentEditorOverviewOwnProps {
	mapId: string;
	casperId: string;
	projection?: string;
	corner: LatLng;
	oppositeCorner: LatLng;
	attachment: FormAttachment;
	mapParams?: IMapAttachmentMapParams;
	file?: File;
	setMapParams: (params: IMapAttachmentMapParams) => void;
	editData: () => void;
	editLocation: () => void;
}

export function AttachmentEditorOverview(
	props: IAttachmentEditorOverviewOwnProps
): ReactElement {
	const {
		mapId,
		casperId,
		projection,
		corner,
		oppositeCorner,
		attachment,
		mapParams,
		setMapParams,
		editData,
		editLocation,
		file
	} = props;

	const { t } = useTranslation('map');

	const markerIcon: DivIcon = divIcon({
		className: 'attachment-icon',
		html: renderToString(<AttachmentPinIcon />)
	});

	const [mapRef, setMapRef] = useState<Map>();
	const [source, setSource] = useState<string>();

	useEffect(() => {
		file
			? setSource(URL.createObjectURL(file))
			: setSource(attachment.location);
	}, [file, attachment]);

	useEffect(() => {
		if (mapRef && mapParams) {
			mapRef.setView(mapParams.center, mapParams.zoom);
		}
		if (mapRef) {
			mapRef.on('zoomend', () => {
				setMapParams({
					center: mapParams ? mapParams.center : mapRef.getCenter(),
					zoom: mapRef.getZoom()
				});
			});
			mapRef.on('dragend', () => {
				setMapParams({
					center: mapRef.getCenter(),
					zoom: mapParams ? mapParams.zoom : mapRef.getZoom()
				});
			});
		}
	}, [mapRef]);

	return (
		<Box className="attachment-editor-overview">
			<Header4>{t('attachments.finish')}</Header4>
			<Box className="attachment-editor-overview-content">
				<Box className="attachment-editor-data">
					{attachment.type === AttachmentType.Image && source && (
						<>
							<Header4>
								{t('attachments.image.imageTitle')}
							</Header4>
							<Box className="attachment-image-container">
								<img
									className="attachment-image"
									src={getPreviewImage(source)}
									alt={attachment.title}
								/>
							</Box>
						</>
					)}
					{attachment.type === AttachmentType.Video && source && (
						<>
							<Header4>{t('attachments.video.urlVideo')}</Header4>
							<Box className="attachment-image-container">
								<VideoPlayer videoUrl={source} />
							</Box>
						</>
					)}
					<Header5>{t('attachments.title')}</Header5>
					<Body1>{attachment.title}</Body1>
					<Header5>{t('attachments.description')}</Header5>
					<Body1>{attachment.description}</Body1>
					<LinkButton onClick={editData}>
						<Edit />
						{t('attachments.editData')}
					</LinkButton>
				</Box>
				<Box className="attachment-editor-map">
					<Header5>{t('attachments.location.locationTitle')}</Header5>
					{attachment.pointOnMap !== null && (
						<>
							<ErrorBoundary>
								<MapViewer
									mapId={mapId}
									casperId={casperId}
									corner={corner}
									oppositeCorner={oppositeCorner}
									projection={projection}
								>
									<MapReference onMapReference={setMapRef} />
									{attachment.pointOnMap !== null && (
										<Marker
											position={attachment.pointOnMap}
											icon={markerIcon}
										/>
									)}
								</MapViewer>
							</ErrorBoundary>
							<LinkButton onClick={editLocation}>
								<Edit />
								{t('attachments.location.changeLocation')}
							</LinkButton>
						</>
					)}
					{attachment.pointOnMap === null && (
						<Box className="attachment-editor-map-empty">
							<Body1>
								{t('attachments.location.notAddLocation')}
							</Body1>
							<Button
								variant="outlined"
								color="primary"
								onClick={editLocation}
							>
								<Add />
								{t('attachments.location.addLocation')}
							</Button>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
}
